import {
  objectTile,
  STORY_ENTRY_MAP_ID,
  storyNpcSchema,
  storyMapSchema,
  stringProperty,
  type CollisionGrid,
  type StoryMap,
  type StoryMapObject,
  type StoryNpc,
} from '@rdd/contracts'
import darkTemple from '../data/story/maps/dark-temple.json'
import desertRuins from '../data/story/maps/desert-ruins.json'
import infirmary from '../data/story/maps/infirmary.json'
import lab from '../data/story/maps/lab.json'
import nightForest from '../data/story/maps/night-forest.json'
import plaza01 from '../data/story/maps/plaza-01.json'
import powerPlant from '../data/story/maps/power-plant.json'
import seaCliffs from '../data/story/maps/sea-cliffs.json'
import shop from '../data/story/maps/shop.json'
import skyCity from '../data/story/maps/sky-city.json'
import skyGarden from '../data/story/maps/sky-garden.json'
import storage from '../data/story/maps/storage.json'
import town from '../data/story/maps/town.json'
import volcano from '../data/story/maps/volcano.json'
import crowler from '../data/story/npcs/crowler.json'
import dorothy from '../data/story/npcs/dorothy.json'
import eisenstein from '../data/story/npcs/eisenstein.json'
import fonda from '../data/story/npcs/fonda.json'
import grandpa from '../data/story/npcs/grandpa.json'
import joey from '../data/story/npcs/joey.json'
import pegasus from '../data/story/npcs/pegasus.json'
import sadie from '../data/story/npcs/sadie.json'

/**
 * Os mapas do modo historia (docs/21 secao 3).
 *
 * **Entrada propria do pacote (`@rdd/content/story`)**, e nao reexportada pelo `index`: o cliente do mundo
 * precisa dos mapas e nao do catalogo de cartas, e importar o `index` levaria o catalogo inteiro para o
 * pacote do navegador. O `api` e o `world-server` importam daqui pelo mesmo caminho.
 *
 * **Validado no carregamento, e com o nome do mapa no erro.** Um mapa invalido derruba o primeiro processo
 * que o importa -- o CI, antes de qualquer publicacao --, e a mensagem diz qual arquivo e por que. Alem do
 * que cada mapa sabe sozinho (`storyMapProblems`), aqui se confere o que so o conjunto sabe: que o portal
 * leva a um mapa que existe, e a um ponto de chegada que existe nele.
 */

/**
 * Os ids de mapa sao estaveis: o ultimo tile de cada conta guarda o id, nunca o arquivo (docs/21 secao 3).
 * Os importados do IDLE FORCE saem de `pnpm content:idle-maps` (ADR 0062).
 */
const SOURCES: Readonly<Record<string, unknown>> = {
  'plaza-01': plaza01,
  town,
  shop,
  storage,
  lab,
  infirmary,
  volcano,
  'sea-cliffs': seaCliffs,
  'night-forest': nightForest,
  'desert-ruins': desertRuins,
  'power-plant': powerPlant,
  'sky-garden': skyGarden,
  'dark-temple': darkTemple,
  'sky-city': skyCity,
}

/** O mapa onde nasce quem nunca entrou (docs/21 secao 3): a cidade. */
export const STORY_DEFAULT_MAP_ID = STORY_ENTRY_MAP_ID

export function parseStoryMap(mapId: string, raw: unknown): StoryMap {
  const parsed = storyMapSchema.safeParse(raw)
  if (!parsed.success) {
    const problems = parsed.error.issues.map((issue) => `${issue.path.join('.') || '(mapa)'}: ${issue.message}`)
    throw new Error(`mapa do modo historia "${mapId}" invalido:\n- ${problems.join('\n- ')}`)
  }
  return parsed.data
}

const objectsOf = (map: StoryMap, name: string): readonly StoryMapObject[] => {
  const layer = map.layers.find((candidate) => candidate.name === name)
  return layer?.type === 'objectgroup' ? layer.objects : []
}

/** Os NPCs do mundo (docs/21 secao 12), pelo id. Um arquivo invalido derruba o carregamento, como um mapa. */
export const STORY_NPCS: ReadonlyMap<string, StoryNpc> = new Map(
  [crowler, dorothy, eisenstein, fonda, grandpa, joey, pegasus, sadie].map((raw) => {
    const parsed = storyNpcSchema.safeParse(raw)
    if (!parsed.success) throw new Error(`NPC do modo historia invalido: ${parsed.error.issues[0]?.message ?? ''}`)
    return [parsed.data.id, parsed.data]
  }),
)

export const storyNpc = (id: string): StoryNpc | undefined => STORY_NPCS.get(id)

/**
 * O que impede o conjunto de mapas de ser jogavel, em frases: o portal que leva a lugar nenhum e o NPC que nao
 * existe no conteudo.
 */
export function storyWorldProblems(
  maps: ReadonlyMap<string, StoryMap>,
  npcs: ReadonlyMap<string, StoryNpc> = STORY_NPCS,
): string[] {
  const problems: string[] = []
  for (const [mapId, map] of maps) {
    for (const npc of objectsOf(map, 'npcs'))
      if (!npcs.has(npc.name ?? '')) problems.push(`o NPC "${npc.name ?? ''}" de "${mapId}" nao existe no conteudo`)
    for (const portal of objectsOf(map, 'portals')) {
      const to = stringProperty(portal, 'to')
      const spawn = stringProperty(portal, 'spawn')
      if (to === undefined || spawn === undefined) continue
      const { x, y } = objectTile(map, portal)
      const target = maps.get(to)
      if (target === undefined)
        problems.push(`o portal de "${mapId}" em (${x}, ${y}) leva ao mapa "${to}", que nao existe`)
      else if (!objectsOf(target, 'spawn').some((point) => point.name === spawn))
        problems.push(`o portal de "${mapId}" em (${x}, ${y}) chega em "${spawn}", que "${to}" nao tem`)
    }
  }
  return problems
}

export function parseStoryWorld(sources: Readonly<Record<string, unknown>>): ReadonlyMap<string, StoryMap> {
  const maps = new Map(Object.entries(sources).map(([mapId, raw]) => [mapId, parseStoryMap(mapId, raw)]))
  const problems = storyWorldProblems(maps)
  if (problems.length > 0) throw new Error(`mundo do modo historia invalido:\n- ${problems.join('\n- ')}`)
  return maps
}

export const STORY_MAPS: ReadonlyMap<string, StoryMap> = parseStoryWorld(SOURCES)

/** O mapa pelo id, ou `undefined` se ele nao existe mais -- um ultimo tile antigo pode apontar para um. */
export const storyMap = (mapId: string): StoryMap | undefined => STORY_MAPS.get(mapId)

/** O nome do mapa para a tela, e de onde a arte veio (docs/21 secao 10). */
export function storyMapInfo(mapId: string): { label: string; origin: 'own' | 'third-party' } | undefined {
  const map = storyMap(mapId)
  if (map === undefined) return undefined
  return {
    label: stringProperty(map, 'label') ?? mapId,
    origin: stringProperty(map, 'origin') === 'third-party' ? 'third-party' : 'own',
  }
}

/** A camada `collision` e o tile de cada NPC, que conta como bloqueado: ele fica parado ali (docs/21 secao 3). */
export function buildCollisionGrid(map: StoryMap): CollisionGrid | undefined {
  const layer = map.layers.find((candidate) => candidate.name === 'collision')
  if (layer?.type !== 'tilelayer') return undefined
  const blocked = layer.data.map((gid) => gid !== 0)
  for (const npc of objectsOf(map, 'npcs')) {
    const { x, y } = objectTile(map, npc)
    blocked[y * map.width + x] = true
  }
  return { width: map.width, height: map.height, blocked }
}

const grids = new Map<string, CollisionGrid>()

/** A colisao do mapa, calculada uma vez por processo. A mesma funcao serve ao cliente e ao servidor. */
export function collisionGrid(mapId: string): CollisionGrid | undefined {
  const cached = grids.get(mapId)
  if (cached !== undefined) return cached
  const map = storyMap(mapId)
  const grid = map === undefined ? undefined : buildCollisionGrid(map)
  if (grid !== undefined) grids.set(mapId, grid)
  return grid
}

/** Os pontos de nascimento, em tiles, na ordem do arquivo. */
export function spawnPoints(mapId: string): ReadonlyArray<{ x: number; y: number }> {
  const map = storyMap(mapId)
  if (map === undefined) return []
  return objectsOf(map, 'spawn').map((point) => objectTile(map, point))
}

/** O ponto de nascimento pelo nome: e onde um portal faz chegar (docs/21 secao 5). */
export function namedSpawn(mapId: string, name: string): { x: number; y: number } | undefined {
  const map = storyMap(mapId)
  const point = map === undefined ? undefined : objectsOf(map, 'spawn').find((candidate) => candidate.name === name)
  return map === undefined || point === undefined ? undefined : objectTile(map, point)
}

/** Os NPCs postos no mapa, em tiles (docs/21 secao 12). O conjunto ja foi conferido: todo NPC existe. */
export function storyNpcsOf(mapId: string): ReadonlyArray<{ npc: StoryNpc; x: number; y: number }> {
  const map = storyMap(mapId)
  if (map === undefined) return []
  return objectsOf(map, 'npcs').flatMap((object) => {
    const npc = storyNpc(object.name ?? '')
    return npc === undefined ? [] : [{ npc, ...objectTile(map, object) }]
  })
}

export type StoryPortal = { x: number; y: number; to: string; spawn: string }

/** Os portais do mapa, em tiles (docs/21 secao 5). O conjunto ja foi conferido: todo destino existe. */
export function storyPortals(mapId: string): readonly StoryPortal[] {
  const map = storyMap(mapId)
  if (map === undefined) return []
  return objectsOf(map, 'portals').map((portal) => ({
    ...objectTile(map, portal),
    to: stringProperty(portal, 'to') ?? '',
    spawn: stringProperty(portal, 'spawn') ?? '',
  }))
}
export { hasWalkSheet, STORY_WALK_SHEET, walkSheetPath } from './story-walk'
